import { useState } from "react";

const FORM_ID = "contact-form";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dahgkjboh653ivfvnumg";

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [count, setCount] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // honeypot check
    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      setCount(0);
      return;
    }

    formData.delete("contact_alt");
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (ok && !isSpam) {
        setStatus("success");
        form.reset();
        setCount(0);
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后重试，或直接发送邮件联系我们。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交没有成功，请检查网络后重试。");
    }
  };

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
            称呼 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼你"
            className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 outline-none focus:border-primary-300"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
            邮箱 <span className="text-primary-500">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复"
            className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 outline-none focus:border-primary-300"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-type" className="block text-sm text-foreground-700 mb-1.5">
          反馈类型
        </label>
        <select
          id="contact-type"
          name="feedback_type"
          defaultValue="信息纠错"
          className="w-full h-11 px-4 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 outline-none focus:border-primary-300"
        >
          <option value="信息纠错">信息纠错</option>
          <option value="内容建议">内容建议</option>
          <option value="收录新话题">收录新话题</option>
          <option value="合作咨询">合作咨询</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
          具体内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          onChange={(e) => setCount(e.target.value.length)}
          placeholder="请说明涉及的合集名称与需要核对或补充的地方（最多 500 字）"
          className="w-full px-4 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 outline-none focus:border-primary-300 resize-none"
        ></textarea>
        <p className="mt-1.5 text-right text-xs text-foreground-400">{count} / 500</p>
      </div>

      <input
        type="text"
        name="contact_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="contact-verify-field"
      />

      {status === "success" && (
        <div className="mt-4 flex items-start gap-2 p-3 rounded-md bg-accent-100 text-accent-900 text-sm">
          <i className="ri-checkbox-circle-line mt-0.5"></i>
          <span>提交成功，感谢你的反馈。我们会尽快核对，并通过邮箱回复你。</span>
        </div>
      )}
      {status === "error" && (
        <div className="mt-4 flex items-start gap-2 p-3 rounded-md bg-primary-100 text-primary-800 text-sm">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span>{formError}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            正在提交
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i>
            提交反馈
          </>
        )}
      </button>
    </form>
  );
}